import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useKiosk } from '../../contexts/KioskContext'
import { useAuth } from '../../contexts/AuthContext'
import ChildSelectionScreen from './ChildSelectionScreen'
import KioskChildView from './KioskChildView'
import ParentPinModal from './ParentPinModal'
import { Toaster } from 'react-hot-toast'

const KioskLayout = () => {
  const { family } = useAuth()
  const { isKioskMode, kioskSession, exitKioskMode, switchChild, updateActivity } = useKiosk()
  const [showPinModal, setShowPinModal] = useState(false)
  const [selectedChild, setSelectedChild] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Force navigation to kiosk page if not already there
  useEffect(() => {
    if (isKioskMode && !location.pathname.includes('/kiosk')) {
      navigate('/kiosk', { replace: true })
    }
  }, [isKioskMode, location.pathname, navigate])

  // Update selected child when session changes
  useEffect(() => {
    if (kioskSession.selectedChild && family?.children) {
      const child = family.children.find(c => c.id === kioskSession.selectedChild)
      setSelectedChild(child)
    } else {
      setSelectedChild(null)
    }
  }, [kioskSession.selectedChild, family?.children])

  // Update activity on user interactions
  useEffect(() => {
    const handleUserActivity = () => {
      updateActivity()
    }

    if (isKioskMode) {
      document.addEventListener('click', handleUserActivity)
      document.addEventListener('touchstart', handleUserActivity)
      document.addEventListener('keypress', handleUserActivity)

      return () => {
        document.removeEventListener('click', handleUserActivity)
        document.removeEventListener('touchstart', handleUserActivity)
        document.removeEventListener('keypress', handleUserActivity)
      }
    }
  }, [isKioskMode, updateActivity])

  // Block navigation attempts
  useEffect(() => {
    if (!isKioskMode) return

    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = 'Kiosk mode is active. Please exit properly.'
      return 'Kiosk mode is active. Please exit properly.'
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isKioskMode])

  const handleChildSelected = (child) => {
    setSelectedChild(child)
    switchChild(child.id)
  }

  const handleSwitchChild = () => {
    setSelectedChild(null)
    switchChild(null)
  }

  const handleExitKiosk = () => {
    setShowPinModal(true)
  }

  const handlePinSuccess = () => {
    setShowPinModal(false)
    exitKioskMode()
    setSelectedChild(null)
    // Navigate to dashboard after successful exit
    navigate('/dashboard', { replace: true })
  }

  if (!isKioskMode) {
    return null
  }

  return (
    <div className="kiosk-mode">
      {/* Prevent text selection and right-click in kiosk mode */}
      <style jsx>{`
        .kiosk-mode {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        .kiosk-mode * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        .kiosk-mode input,
        .kiosk-mode textarea {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }
        /* Hide scrollbars */
        .kiosk-mode::-webkit-scrollbar {
          display: none;
        }
        .kiosk-mode {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {selectedChild ? (
        <KioskChildView
          child={selectedChild}
          onSwitchChild={handleSwitchChild}
          onExitKiosk={handleExitKiosk}
        />
      ) : (
        <ChildSelectionScreen
          onChildSelected={handleChildSelected}
          onExitKiosk={handleExitKiosk}
        />
      )}

      <ParentPinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSuccess={handlePinSuccess}
      />

      {/* Kiosk-specific toast configuration */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--kiosk-toast-bg)',
            color: 'var(--kiosk-toast-color)',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '1px solid var(--kiosk-toast-border)',
            fontSize: '16px',
            fontWeight: '500',
            maxWidth: '400px',
          },
          success: {
            iconTheme: {
              primary: '#6ECC78',
              secondary: '#fff'
            }
          }
        }}
      />

      {/* CSS Variables for Kiosk Toast Theming */}
      <style jsx>{`
        :global(:root) {
          --kiosk-toast-bg: #fff;
          --kiosk-toast-color: #424242;
          --kiosk-toast-border: #E2E8F0;
        }
        :global(.dark) {
          --kiosk-toast-bg: #374151;
          --kiosk-toast-color: #F9FAFB;
          --kiosk-toast-border: #4B5563;
        }
      `}</style>

      {/* Disable context menu and other interactions in kiosk mode */}
      <div
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onSelectStart={(e) => e.preventDefault()}
        className="fixed inset-0 pointer-events-none z-0"
      />

      {/* Full screen overlay to prevent interaction with browser UI */}
      <div className="fixed inset-0 z-[-1] bg-white dark:bg-gray-900" />
    </div>
  )
}

export default KioskLayout